/**
 * Data for the PDApps shell (#1484).
 *
 *   - History (personal, every org, links too): `/api/pdapps-shell/history` — pins
 *     plus the recent entries, each already judged by the server as the identity it
 *     was opened as. It follows the ACCOUNT, so it reads the same in every org.
 *   - Shared-with-me links: `/api/pdapps-shell/shared-links` — app links that name
 *     the active identity and would run for it.
 *   - The active org's own apps: the SAME two listings Drive → PDApps reads
 *     (`useProjects` + `useIncomingSharedProjects`), permission-filtered there.
 *
 * Nothing here decides access: every list comes judged from the server, and Run /
 * Share go through the routes and dialogs they always did.
 */
import { useQuery, type QueryClient } from '@tanstack/react-query'
import { request, getActiveOrgEmail, getActiveTenantId } from '@papan/api-client'
import { appDisplayName, isMiniappFolder, isSharedPointer, stripPointerSuffix } from '@papan/types'
import { isMyProject, type ProjectItem } from '../home/data'

/** One tile from the shell's own endpoints (the server's `PdappEntryDto`). */
export interface PdappEntry {
  key: string
  source: 'history' | 'shared'
  kind: 'app' | 'link'
  name: string
  icon: string | null
  orgName: string | null
  pinned: boolean
  openedAt: string | null
  /** When it last came into Recent (opened, or un-favorited); null/absent = not in Recent. */
  recentAt?: string | null
  /** Link entries: `public` (anyone with the URL) or `restricted` (the link names who may). */
  audience?: 'public' | 'restricted'
  /** Link entries: the link is the active org's own (internal); else another org's or a Personal space's (external). */
  fromHere?: boolean
  /** Shared links: the other links' handles this card stands for (the "seen" memory counts them all). */
  alsoKeys?: string[]
  /** `switchTo`: the identity to make this org's choice before a Run (see the server's `PdappEntryDto`). */
  app?: { tenantId: string; personal: boolean; appId: string; mounted: boolean; switchTo?: string }
}

export interface PdappHistoryPage { pinned: PdappEntry[]; recent: PdappEntry[]; next: string | null }

// Under `['projects', …]` with the identity in the key, so the app-wide identity
// guard (`IDENTITY_SCOPED_KEYS`) covers them: a switch never shows one identity's
// answer under another's key.
const identity = () => [getActiveTenantId(), getActiveOrgEmail()] as const
export const pdappsKeys = {
  all: ['projects', 'pdapps-shell'] as const,
  history: () => ['projects', 'pdapps-shell', 'history', ...identity()] as const,
  sharedLinks: (sameOrg: boolean) => ['projects', 'pdapps-shell', sameOrg ? 'shared-links-org' : 'shared-links', ...identity()] as const,
}

const BASE = '/api/pdapps-shell'
const JSON_HEADERS = { 'Content-Type': 'application/json' }

export const fetchHistoryPage = (before?: string | null) =>
  request<PdappHistoryPage>(`${BASE}/history${before ? `?before=${encodeURIComponent(before)}` : ''}`)

/** Pins + the first page of the recent history. */
export function usePdappHistory(enabled = true) {
  return useQuery<PdappHistoryPage>({
    queryKey: pdappsKeys.history(),
    queryFn: () => fetchHistoryPage(null),
    staleTime: 30_000,
    enabled,
  })
}

/** App links that name the active identity (`sameOrg`: this org's only — the drive page). */
export function useSharedLinkApps(sameOrg: boolean, enabled = true) {
  return useQuery<PdappEntry[]>({
    queryKey: pdappsKeys.sharedLinks(sameOrg),
    queryFn: async () => (await request<{ entries: PdappEntry[] }>(`${BASE}/shared-links${sameOrg ? '?sameOrg=1' : ''}`)).entries,
    staleTime: 30_000,
    enabled,
  })
}

/**
 * Re-read the HISTORY after a write to it (an open, a pin, a removal) — and the
 * shared-links lists, whose cards carry the person's own entry for each link (opened
 * when, starred), so they change with it too. CANCEL first: a read already
 * in flight (the window was just opened) started before this write, and a refresh
 * while a first load is running is folded into that same read instead of starting
 * a new one — the window would show the list without the write until its next
 * refresh. Only a SHOWN window refetches (its query is `enabled` by `deckActive`);
 * a hidden one is just marked stale and reads again when it is shown.
 */
const refresh = (qc: QueryClient): Promise<void> => {
  const queryKey = ['projects', 'pdapps-shell', 'history'] as const
  const history = qc.cancelQueries({ queryKey }).then(() => qc.invalidateQueries({ queryKey }))
  // The shared lists after it, as on a first load — never two heavy reads at once.
  void history
    .then(() => Promise.all(['shared-links', 'shared-links-org'].map((list) => {
      const key = ['projects', 'pdapps-shell', list] as const
      return qc.cancelQueries({ queryKey: key }).then(() => qc.invalidateQueries({ queryKey: key }))
    })))
    .catch(() => { /* a failed re-read leaves the lists as they were */ })
  // Resolves once the HISTORY is read again (a star's next request acts on the real state).
  return history.catch(() => {})
}

/**
 * "I opened this app now" (the active org). Fire-and-forget — a lost write only
 * costs the entry; the server checks the app is readable before it stores it.
 */
export function recordAppOpen(queryClient: QueryClient, appId: string): void {
  void request(`${BASE}/history/app`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ appId }) })
    .then(() => refresh(queryClient))
    .catch(() => {})
}

export async function pinActiveApp(qc: QueryClient, appId: string): Promise<void> {
  await request(`${BASE}/pins/app`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ appId }) })
  await refresh(qc)
}

export async function pinSharedLink(qc: QueryClient, key: string, pinned = true): Promise<void> {
  await request(`${BASE}/links/${encodeURIComponent(key)}/pin`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ pinned }) })
  await refresh(qc)
}

/**
 * Show a star change at once, in the cached history — the server's answer and the re-read
 * (which judges every entry) follow. Unpinning puts the entry first in Recent, as the server
 * does (in case the person regrets it). Returns how to put the cache back if the write fails.
 */
export async function showEntryPinned(qc: QueryClient, key: string, pinned: boolean): Promise<() => void> {
  const queryKey = pdappsKeys.history()
  await qc.cancelQueries({ queryKey })
  const before = qc.getQueryData<PdappHistoryPage>(queryKey)
  if (!before) return () => {}
  const entry = before.pinned.find(e => e.key === key) ?? before.recent.find(e => e.key === key)
  if (!entry) return () => {}
  const next: PdappHistoryPage = pinned
    ? {
        ...before,
        pinned: before.pinned.some(e => e.key === key) ? before.pinned : [...before.pinned, { ...entry, pinned: true }],
        recent: before.recent.map(e => (e.key === key ? { ...e, pinned: true } : e)),
      }
    : {
        ...before,
        pinned: before.pinned.filter(e => e.key !== key),
        recent: [{ ...entry, pinned: false, recentAt: new Date().toISOString() }, ...before.recent.filter(e => e.key !== key)],
      }
  qc.setQueryData<PdappHistoryPage>(queryKey, next)
  return () => qc.setQueryData<PdappHistoryPage>(queryKey, before)
}

export async function setEntryPinned(qc: QueryClient, key: string, pinned: boolean): Promise<void> {
  const undo = await showEntryPinned(qc, key, pinned)
  try {
    await request(`${BASE}/history/${encodeURIComponent(key)}`, { method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ pinned }) })
  } catch (err) {
    undo()
    throw err
  }
  await refresh(qc)
}

/**
 * Show the Favorites deck in `keys`' order at once (the pinned entries' keys, first to
 * last), in the cached history. A read already on its way is cancelled first — it was
 * asked before this order and would put the old one back when it lands.
 */
export async function showPinOrder(qc: QueryClient, keys: readonly string[]): Promise<void> {
  const queryKey = pdappsKeys.history()
  await qc.cancelQueries({ queryKey })
  const before = qc.getQueryData<PdappHistoryPage>(queryKey)
  if (!before) return
  const at = new Map(keys.map((k, i) => [k, i]))
  const pinned = [...before.pinned].sort((a, b) => (at.get(a.key) ?? Infinity) - (at.get(b.key) ?? Infinity))
  qc.setQueryData<PdappHistoryPage>(queryKey, { ...before, pinned })
}

/** Save the Favorites deck's order on the server (the caller sends one at a time). */
export async function putPinOrder(keys: readonly string[]): Promise<void> {
  await request(`${BASE}/pins/order`, { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ ids: keys }) })
}

/** Read the history again (the server's own answer — after a refused save). */
export const reloadHistory = (qc: QueryClient) => refresh(qc)

/** Re-read the HISTORY only — after a deck save (the order lives there; the shared lists hold none). */
export function reloadHistoryOnly(qc: QueryClient): void {
  const queryKey = ['projects', 'pdapps-shell', 'history'] as const
  void qc.cancelQueries({ queryKey }).then(() => qc.invalidateQueries({ queryKey })).catch(() => {})
}

export async function removeEntry(qc: QueryClient, key: string): Promise<void> {
  await request(`${BASE}/history/${encodeURIComponent(key)}`, { method: 'DELETE' })
  refresh(qc)
}

export async function clearHistory(qc: QueryClient): Promise<void> {
  await request(`${BASE}/history`, { method: 'DELETE' })
  refresh(qc)
}

/**
 * Open a link app. The tab is opened FIRST, on the click (a tab opened after an
 * await is a popup the browser blocks), cut off from this page (`opener = null`),
 * and only then pointed at the address the server hands back after re-judging
 * the link. A refusal closes the tab and throws.
 */
/**
 * A link app's own address, to pass on (the Share modal's Copy link) — asked for one entry at a
 * time, judged again by the server; never part of a list. `audience` says who it opens for.
 */
export async function linkAppAddress(entry: PdappEntry): Promise<{ url: string; audience: 'public' | 'restricted' }> {
  return request<{ url: string; audience: 'public' | 'restricted' }>(`${BASE}/links/${encodeURIComponent(entry.source === 'history' ? 'h' : entry.key)}/address`, {
    method: 'POST', headers: JSON_HEADERS,
    body: JSON.stringify(entry.source === 'history' ? { historyId: entry.key } : {}),
  })
}

export async function openLinkApp(qc: QueryClient, entry: PdappEntry): Promise<void> {
  const tab = window.open('about:blank', '_blank')
  if (tab) tab.opener = null
  try {
    const { url } = await request<{ url: string }>(`${BASE}/links/${encodeURIComponent(entry.source === 'history' ? 'h' : entry.key)}/open`, {
      method: 'POST', headers: JSON_HEADERS,
      body: JSON.stringify(entry.source === 'history' ? { historyId: entry.key } : {}),
    })
    if (tab) tab.location.replace(url)
    else window.location.assign(url)
    refresh(qc)
  } catch (err) {
    tab?.close()
    throw err
  }
}

/**
 * #538 — the identity bar's "Made by": where a link's PDApp sits in the person's own drive, as a
 * drive address that reveals it — or null when it isn't in their drive (the server answers that
 * the same way whatever the reason). The bar hands the link over in this page's FRAGMENT
 * (`/~/apps#locate=<token>`), which no server sees; it is read once and cleared.
 */
export async function locateLinkApp(token: string): Promise<string | null> {
  try {
    return (await request<{ path: string }>(`${BASE}/locate?link=${encodeURIComponent(token)}`)).path
  } catch (err) {
    if ((err as { name?: string } | null)?.name === 'AbortError') throw err
    return null
  }
}

/** The `#locate=<token>` the bar opened this page with, taken off the address — or null. */
export function takeLocateFragment(): string | null {
  const m = /^#locate=([a-z0-9-]{1,120})$/.exec(window.location.hash)
  if (!m) return null
  window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search)
  return m[1]!
}

// ─── The active org's own apps (from the drive's listings) ────────────────

/** Mine = in the viewer's own space, or made by them; Shared = someone else's app
 *  in the org they can open, or one another org shared in (a `.shared` pointer). */
export type OrgAppGroup = 'mine' | 'shared'

export function isAppItem(p: ProjectItem): boolean {
  return isMiniappFolder(stripPointerSuffix(p.name))
}

export function orgAppGroupOf(p: ProjectItem, orgEmail: string | null | undefined, isOrg: boolean): OrgAppGroup {
  if (isSharedPointer(p.name)) return 'shared'
  if (isMyProject(p.path, orgEmail, isOrg)) return 'mine'
  // Made by the viewer but kept in a shared folder (a team's, the org's) is still
  // theirs — "Shared with me" means someone ELSE's app. The creator is this org's
  // membership email, the same identity as `orgEmail`.
  const creator = p.owner?.email?.toLowerCase()
  return creator && orgEmail && creator === orgEmail.toLowerCase() ? 'mine' : 'shared'
}

/** The name a person reads: no `.pdapp`, no `.shared`. */
export function appName(p: ProjectItem): string {
  return appDisplayName(stripPointerSuffix(p.name))
}

const matches = (name: string, q: string) => !q || name.toLowerCase().includes(q)

/**
 * The org part of the shell: the drive's app rows (apps only, de-duplicated,
 * admin-only noise hidden as the drive page does by default) split into Mine and
 * Shared, A→Z within each; `query` filters by name.
 */
export function buildOrgApps(
  items: readonly ProjectItem[],
  { orgEmail, isOrg, query = '' }: { orgEmail: string | null | undefined; isOrg: boolean; query?: string },
): Record<OrgAppGroup, ProjectItem[]> {
  const q = query.trim().toLowerCase()
  const seen = new Set<string>()
  const out: Record<OrgAppGroup, ProjectItem[]> = { mine: [], shared: [] }
  for (const p of items) {
    if (!isAppItem(p) || seen.has(p.folderId)) continue
    seen.add(p.folderId)
    if (isOrg && p.hiddenWithoutAdminView) continue
    if (!matches(appName(p), q)) continue
    out[orgAppGroupOf(p, orgEmail, isOrg)].push(p)
  }
  const byName = (a: ProjectItem, b: ProjectItem) => appName(a).localeCompare(appName(b), undefined, { sensitivity: 'base' })
  out.mine.sort(byName)
  out.shared.sort(byName)
  return out
}

/**
 * The Recent page's chips: All · here (the org the person is in now — or Personal) ·
 * elsewhere (another org, or Personal while in an org) · links (opened through a link,
 * public or naming them — a link entry carries no org id, so it is its own group).
 */
export const RECENT_FILTERS = ['all', 'here', 'elsewhere', 'links'] as const
export type RecentFilter = (typeof RECENT_FILTERS)[number]

/** Which chip (besides All) an entry belongs to, for the person now in `tenantId`. */
export function recentFilterOf(entry: PdappEntry, tenantId: string): Exclude<RecentFilter, 'all'> {
  if (entry.kind === 'link' || !entry.app) return 'links'
  return entry.app.tenantId === tenantId ? 'here' : 'elsewhere'
}

/** Filter shell entries by name. */
export function filterEntries(entries: readonly PdappEntry[], query: string): PdappEntry[] {
  const q = query.trim().toLowerCase()
  return q ? entries.filter((e) => matches(e.name, q)) : [...entries]
}
